import { useState } from "react";
import { BACKEND_URL } from "@/lib/api";
import axios from "axios";
import { Shield, AlertCircle, CheckCircle2, ExternalLink, Loader2 } from "lucide-react";

const CONSENT_TEXT =
  "I consent to Aylesbury Hindu Temple (AHT) volunteers submitting a response to the " +
  "Buckinghamshire Council Local Plan Sites Survey on my behalf. I understand that AHT " +
  "(charity no. 1209109, www.aylesburyhindutemple.org) will store my contact details, consent record, " +
  "and IP address for GDPR and audit purposes. I can request deletion of " +
  "my data at any time by contacting AHT via www.aylesburyhindutemple.org.";

export default function VolunteerConsent() {
  const [form, setForm] = useState({
    email: "",
    mobile: "",
    name: "",
    postcode: "",
  });
  const [consentGiven, setConsentGiven] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState(null);

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const validate = () => {
    const errs = {};
    const hasEmail = form.email.trim().length > 0;
    const hasMobile = form.mobile.trim().length > 0;

    if (!hasEmail && !hasMobile) {
      errs.contact = "Please provide an email address or mobile number so we can contact you.";
    }
    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errs.email = "Please enter a valid email address.";
    }
    if (hasMobile && !/^[\d\s+\-()]{7,15}$/.test(form.mobile.trim())) {
      errs.mobile = "Please enter a valid mobile number.";
    }
    if (!consentGiven) {
      errs.consent = "Please agree to the consent statement to continue.";
    }
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    setServerError(null);
    try {
      await axios.post(`${BACKEND_URL}/api/public/volunteer-consent`, {
        email: form.email.trim() || null,
        mobile: form.mobile.trim() || null,
        name: form.name.trim() || null,
        postcode: form.postcode.trim() || null,
        consent_given: true,
        consent_text: CONSENT_TEXT,
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setServerError(detail);
    } finally {
      setLoading(false);
    }
  };

  // ── Success state ───────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center space-y-4">
          <CheckCircle2 size={48} className="text-green-500 mx-auto" />
          <h2 className="text-xl font-bold text-gray-900">Thank you!</h2>
          <p className="text-gray-600 leading-relaxed">
            Your details have been recorded. An AHT volunteer will submit a
            response to the council consultation on your behalf.
          </p>
          {form.email && (
            <p className="text-sm text-gray-500">
              A confirmation has been sent to <strong>{form.email}</strong>.
            </p>
          )}
          <a
            href="/"
            className="inline-block mt-2 text-indigo-600 hover:underline text-sm"
          >
            ← Back to main page
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur border-b border-slate-200 shadow-sm">
        <div className="max-w-lg mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <img
              src="/aht_full_brand.png"
              alt="Aylesbury Hindu Temple"
              className="h-12 w-auto object-contain shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-slate-900">Aylesbury Hindu Temple</h1>
              <p className="text-xs text-slate-600 mt-0.5">Volunteer Submission — Council Consultation</p>
            </div>
          </div>
          <a
            href="https://www.aylesburyhindutemple.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium text-orange-600 hover:text-orange-700 hover:underline shrink-0"
          >
            aylesburyhindutemple.org ↗
          </a>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-10 space-y-6">
        {/* Intro */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-full">
            🛕 Aylesbury Hindu Temple CIO · Charity no. 1209109
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900">
            Can't use a laptop or desktop?
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Leave your details below and one of our volunteers will submit a
            response to the council's Local Plan Sites Survey on your behalf.
            Your consent is required and your data will be stored securely.
          </p>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-7 space-y-5">

          {/* Contact (email or mobile — one required) */}
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-700">
              Your contact details <span className="text-red-500">*</span>
            </p>
            <p className="text-xs text-gray-400">Please provide at least one — email is preferred.</p>
            {errors.contact && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle size={11} /> {errors.contact}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@example.com"
              className={`w-full rounded-xl border px-4 py-2.5 text-sm text-slate-900 placeholder-slate-500 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-400 ${errors.email ? "border-red-400 bg-red-50" : "border-slate-300"}`}
            />
            {errors.email && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle size={11} /> {errors.email}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 shrink-0">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile number
            </label>
            <input
              type="tel"
              value={form.mobile}
              onChange={update("mobile")}
              placeholder="e.g. 07700 900000"
              className={`w-full rounded-xl border px-4 py-2.5 text-sm text-slate-900 placeholder-slate-500 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-400 ${errors.mobile ? "border-red-400 bg-red-50" : "border-slate-300"}`}
            />
            {errors.mobile && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle size={11} /> {errors.mobile}
              </p>
            )}
          </div>

          <div className="border-t border-gray-100 pt-4 space-y-4">
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
              Optional — helps personalise your submission
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your name <span className="text-gray-400 text-xs font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={update("name")}
                placeholder="Your full name"
                className="w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postcode <span className="text-gray-400 text-xs font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={form.postcode}
                onChange={update("postcode")}
                placeholder="e.g. HP20 1AA"
                className="w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
          </div>

          {/* GDPR Consent */}
          <label
            className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
              consentGiven
                ? "border-green-300 bg-green-50"
                : errors.consent
                ? "border-red-300 bg-red-50"
                : "border-gray-200 bg-gray-50 hover:bg-gray-100"
            }`}
          >
            <input
              type="checkbox"
              className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
              checked={consentGiven}
              onChange={(e) => setConsentGiven(e.target.checked)}
            />
            <span className="text-xs text-gray-600 leading-relaxed">
              <Shield size={12} className="inline mr-1 text-indigo-500 align-middle" />
              {CONSENT_TEXT}
            </span>
          </label>
          {errors.consent && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle size={11} /> {errors.consent}
            </p>
          )}

          {/* Server error */}
          {serverError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
              <AlertCircle size={15} className="shrink-0 mt-0.5" />
              <span>{serverError}</span>
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-bold py-4 rounded-xl text-base transition-colors shadow-md"
          >
            {loading ? (
              <><Loader2 size={18} className="animate-spin" /> Submitting…</>
            ) : (
              "Submit my consent"
            )}
          </button>

          <p className="text-center text-xs text-gray-400">
            Your data is processed under UK GDPR by Aylesbury Hindu Temple CIO (charity no. 1209109).
            You may request deletion at any time.
          </p>
        </div>

        {/* Can use a laptop? */}
        <div className="text-center text-sm text-gray-500">
          Got a laptop or desktop available?{" "}
          <a href="/" className="text-indigo-600 hover:underline font-medium">
            Submit it yourself in 15 minutes →
          </a>
        </div>

        {/* About AHT */}
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 text-sm text-amber-900 space-y-1">
          <p className="font-semibold">About Aylesbury Hindu Temple CIO</p>
          <p className="text-xs leading-relaxed text-amber-800">
            Aylesbury Hindu Temple Charitable Incorporated Organisation (charity no. 1209109)
            is seeking a permanent site within or nearby Aylesbury for a Hindu Mandir, community
            hall, and cultural centre. The temple will serve as a place of worship, cultural
            education, food bank support, and community events open to all.
          </p>
          <a
            href="https://www.aylesburyhindutemple.org"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline font-medium mt-1"
          >
            <ExternalLink size={11} /> www.aylesburyhindutemple.org
          </a>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 pb-6 mt-6">
          Built by{" "}
          <a
            href="https://scaleright.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline text-indigo-400"
          >
            ScaleRight.ai
          </a>
        </p>
      </main>
    </div>
  );
}
